import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { Storage } from '@ionic/storage-angular';
import { SecureCredentialsService } from './credenciais-service/SecureCredentialsService';
import { isPlatform } from '@ionic/angular';
import { PerfilService, PermissaoInfo, ProgramaDTO } from './perfil-usuario-service/perfil-usuario.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  private secureCredentials = inject(SecureCredentialsService);
  private perfilService = inject(PerfilService);

  constructor(
    private http: HttpClient,
    private router: Router,
    private storage: Storage
  ) {
    this.initStorage();
  }

  async initStorage() {
    await this.storage.create();
  }

  async login(identificador: string, senha: string): Promise<string> {
  const body = { login: identificador, senha };

  const response = await firstValueFrom(
    this.http.post(`/auth/login`, body, { responseType: 'text' })
  );

  const token = (response || '').trim().replace(/(\r\n|\n|\r)/gm, '');

  if (!token || token.length < 20) {
    throw new Error('Servidor não retornou um token válido.');
  }

  const decoded: any = jwtDecode(token);

  // 🔥 LOG PARA VER O QUE TEM NO TOKEN
  console.log('🔑 Token decodificado:', decoded);
  console.log('🔑 userId do token:', decoded.userId);
  console.log('🔑 sub do token:', decoded.sub);

  // 🔥 TENTA PEGAR O userId DE VÁRIAS FORMAS
  const userId = decoded.userId || decoded.sub || decoded.id || decoded.matricula || '';

  await this.storage.set('token', token);
  await this.storage.set('identificador', identificador);
  await this.storage.set('dataLogin', this.hojeBrasil());
  await this.storage.set('role', decoded.authorities?.[0] || '');
  await this.storage.set('userId', userId);  // 🔥 SALVA O userId

  // 🔥 SE TIVER tenant, salva também
  if (decoded.tenant_id && decoded.tenant_id.length == 1) {
    await this.storeTenant(decoded.tenant_id[0].tenant_id, decoded.tenant_id[0].versao);
  }

  console.log('✅ userId salvo no storage:', userId);

  return token;
}
  
 async storeTenant(tenant_id: any, versao: any) {
      await this.storage.set('tenant_id', tenant_id);
      await this.storage.set('versao', versao);
      const userId = await this.storage.get("userId");
      const programas = await this.perfilService.buscarPermissoesUsuario(+userId).toPromise() || [];
    const permissoesCache: { [key: string]: any } = {};
      programas.forEach(programa => {
        programa.componentes.forEach(comp => {
          permissoesCache[comp.nome] = {
            nome: comp.nome,
            habilitado: comp.habilitado,
            visivel: comp.visivel,
            tipo: comp.tipo
          };
        });
      });
      await this.storage.set('permissoes', permissoesCache);     
  }

 async getTenantId() {
  return await this.storage.get('tenant_id');
 }
  

  enviarEmailPrimeiroAcesso(identificador: string): Observable<string> {
    return this.http.post(
      `/auth/primeiro-acesso`,
      {},
      {
        params: { email: identificador },
        responseType: 'text'
      }
    );
  }

  alterarSenha(identificador: string, senha: string): Observable<string> {

    const body = { identificador, senha };

    return this.http.post(
      `/auth/altera-senha`,
      body,
      { responseType: 'text' }
    );
  }

  async getToken(): Promise<string> {
    return (await this.storage.get('token')) || '';
  }

  async getRole(): Promise<string> {
    return (await this.storage.get('role')) || '';
  }

  async getIdentificador(): Promise<string> {
    return (await this.storage.get('identificador')) || '';
  }

  async logout() {
    await this.storage.clear();
    this.router.navigate(['/auth/login']);
  }

  async isAuthenticated(): Promise<boolean> {
    return !!(await this.storage.get('token'));
  }

  async isLoginValidoHoje(): Promise<boolean> {
    const token = await this.storage.get('token');
    const dataLogin = await this.storage.get('dataLogin');
    return !!token && dataLogin === this.hojeBrasil();
  }

  async getDadosLogin(): Promise<{ identificador: string, data: string } | null> {

    const identificador = await this.storage.get('identificador');
    const dataLogin = await this.storage.get('dataLogin');

    if (identificador && dataLogin) {
      return { identificador, data: dataLogin };
    }

    return null;
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  hojeBrasil(): string {
    const d = new Date();
    const ano = d.getFullYear();
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  async saveCredentials(identificador: string, senha: string): Promise<boolean> {
    return this.secureCredentials.saveCredentials(identificador, senha);
  }

  // 🔥 NOVO: Método para recuperar credenciais
  async getCredentials(): Promise<{ identificador: string; senha: string } | null> {
    return this.secureCredentials.getCredentials();
  }

  // 🔥 NOVO: Método para limpar credenciais
  async clearCredentials(): Promise<boolean> {
    return this.secureCredentials.clearCredentials();
  }

  // 🔥 NOVO: Verifica se tem credenciais salvas
  async hasCredentials(): Promise<boolean> {
    return this.secureCredentials.hasCredentials();
  }

  async getUserId(): Promise<string | number> {
    return (await this.storage.get('userId')) || '';
  }

  async getPermisoesPrograma(): Promise<{ [key: string]: PermissaoInfo } | null> {
    return (await this.storage.get('permissoes')) || null;
  }

}